import React from 'react';
import { useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { PageShell, BackHeader, SectionTitle, MetricCard, MetricGrid, Text, Button, Icon, StatCell, StatsWrap, Badge, BADGE_TONE_COLOR, Eyebrow, DrillChevron, Skeleton } from '../../components';
import { useMockEndpoint } from '../../hooks/useMockEndpoint';
import { useAcquisitionChannelGate } from '../../hooks/useAcquisitionChannelGate';
import { ROUTE } from '../../routes';
import { influencersData } from '../../mock-data/influencers';
import type { Influencer, InfluencerType } from '../../mock-data/types';
import { INFLUENCER_FILTERS } from './constants';
import { InfluencerForm } from './components/InfluencerForm';
import {
  HeaderRow,
  HeaderLeft,
  HeaderTitle,
  Count,
  Panel,
  Filters,
  FilterLbl,
  FilterChips,
  Pill,
  InfList,
  InfCard,
  InfTop,
  InfName,
  NoteRow,
  BoxedStatGrid,
} from './influencers.styles';

export type InfluencersProps = {
  styleText?: string;
  className?: string;
};

type FilterValue = InfluencerType | 'all';

/**
 * Influencers page: this-month overview, the influencer list (filterable by deal
 * type, each card colour-coding ROI/notes), and the "Add / Edit Influencer" form,
 * which opens as a centred modal (desktop) or bottom sheet (mobile): "Create"
 * opens the empty variant, tapping a card opens it pre-filled to edit.
 */
const Influencers = ({ styleText = '', className = '' }: InfluencersProps): React.ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const data = influencersData;

  // Closed UI vocabulary (overview + per-card metric labels, deal-type / filter
  // labels) sourced from mock-data today, an API later. Translated reactively at
  // the render site; unknown values fall through untranslated rather than blank.
  const cardLabels: Record<string, string> = {
    'Total Spend': t('Total Spend'),
    'Blended CPA': t('Blended CPA'),
    All: t('All'),
    CPA: t('CPA'),
    'Rev Share': t('Rev Share'),
    Hybrid: t('Hybrid'),
    Regs: t('Regs'),
    FTDs: t('FTDs'),
    'Reg → FTD': t('Reg → FTD'),
    'Avg Dep': t('Avg Dep'),
    'Cost / FTD': t('Cost / FTD'),
    'Rev / FTD': t('Rev / FTD'),
    ROI: t('ROI'),
    'Total Cost': t('Total Cost'),
    'True Cost / FTD': t('True Cost / FTD'),
  };
  const cardLabel = (label: string): string => cardLabels[label] ?? label;

  // The this-month overview and the influencer roster are separate endpoints, so
  // they fill in on their own randomised delays; every figure within each shares
  // the one wait. Swap each for a real query hook of the same `{ data, loading }`
  // shape later. (Identity, names and types, stays; only the figures load in.)
  const overview = useMockEndpoint(data.overview);
  const roster = useMockEndpoint(data.influencers);

  // Filter + which modal is open both live in the URL, so a view is shareable and
  // the browser Back button closes the modal. `?filter=` is the list filter;
  // `?influencer=new` opens the create modal and `?influencer=<id>` the edit modal.
  const rawFilter = searchParams.get('filter');
  const selectedFilter: FilterValue =
    rawFilter === 'cpa' || rawFilter === 'rev-share' || rawFilter === 'hybrid' ? rawFilter : 'all';

  const formParam = searchParams.get('influencer');
  const editing = formParam && formParam !== 'new' ? data.influencers.find((i) => i.id === formParam) : undefined;
  const formOpen = formParam === 'new' || editing !== undefined;

  // Mutate the query string while preserving the other params. Push by default so
  // Back undoes the change; replace to silently tidy the URL.
  const setParams = React.useCallback(
    (mutate: (params: URLSearchParams) => void, replace: boolean): void => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace },
      );
    },
    [setSearchParams],
  );

  // True while a modal we opened in this session sits on top of the history stack,
  // so closing can cleanly pop it (Back-equivalent). A directly-shared modal link
  // has nothing of ours to pop, so we strip the param in place instead.
  const openedHereRef = React.useRef(false);

  // A stale shared link to a removed influencer (`?influencer=<unknown>`) resolves to
  // no modal; drop the dangling param so the URL stays honest.
  React.useEffect(() => {
    if (formParam && formParam !== 'new' && !data.influencers.some((i) => i.id === formParam)) {
      setParams((p) => p.delete('influencer'), true);
    }
  }, [formParam, data.influencers, setParams]);

  const onFilterSelect = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const value = event.currentTarget.dataset.value;
      setParams((p) => {
        if (value === 'cpa' || value === 'rev-share' || value === 'hybrid') p.set('filter', value);
        else if (value === 'all') p.delete('filter');
      }, false);
    },
    [setParams],
  );

  const onCreate = React.useCallback(() => {
    openedHereRef.current = true;
    setParams((p) => p.set('influencer', 'new'), false);
  }, [setParams]);

  const onEdit = React.useCallback(
    (influencer: Influencer) => {
      openedHereRef.current = true;
      setParams((p) => p.set('influencer', influencer.id), false);
    },
    [setParams],
  );

  const onCloseForm = React.useCallback(() => {
    if (openedHereRef.current) {
      openedHereRef.current = false;
      navigate(-1);
    } else {
      setParams((p) => p.delete('influencer'), true);
    }
  }, [navigate, setParams]);

  const visible = selectedFilter === 'all' ? data.influencers : data.influencers.filter((i) => i.type === selectedFilter);

  return (
    <PageShell styleText={styleText} className={className}>
      <BackHeader title={t('Influencers')} />

      <div>
        <SectionTitle title={t("This month's Overview")} />
        <MetricGrid>
          {data.overview.map((metric) => (
            <MetricCard key={metric.id} label={cardLabel(metric.label)} value={metric.value} change={metric.change} changeType={metric.changeType} vs={t('vs last month')} loading={overview.loading} />
          ))}
        </MetricGrid>
      </div>

      <HeaderRow>
        <HeaderLeft>
          <HeaderTitle text="Your Influencers" tag="span" />
          <Count>{roster.loading ? <Skeleton width="2ch" height="0.8em" radius={5} /> : data.count}</Count>
        </HeaderLeft>
        <Button label={t('Create')} variant="primary" icon="plus" onClick={onCreate} />
      </HeaderRow>

      <Panel>
        <Filters>
          <FilterLbl text="Filter:" tag="span" />
          <FilterChips>
            {INFLUENCER_FILTERS.map((filter) => (
              <Pill
                key={filter.value}
                type="button"
                $on={selectedFilter === filter.value}
                aria-pressed={selectedFilter === filter.value}
                data-value={filter.value}
                onClick={onFilterSelect}
              >
                <Text text={cardLabel(filter.label)} tag="span" />
              </Pill>
            ))}
          </FilterChips>
        </Filters>

        <InfList>
          {visible.map((inf) => (
            <InfCard
              key={inf.id}
              role="button"
              tabIndex={0}
              aria-label={t('Edit {{name}}', { name: inf.name })}
              onClick={() => onEdit(inf)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onEdit(inf);
                }
              }}
            >
              <InfTop>
                <InfName text={inf.name} tag="span" />
                <DrillChevron />
              </InfTop>
              <Eyebrow text={cardLabel(inf.typeLabel)} detail={inf.typeDetail} detailAccent={inf.typeDetailAccent} />
              <StatsWrap>
                <BoxedStatGrid $columns={4}>
                  {inf.stats.map((stat) => (
                    <StatCell key={stat.id} label={cardLabel(stat.label)} value={stat.value} tone={stat.tone} loading={roster.loading} />
                  ))}
                </BoxedStatGrid>
              </StatsWrap>
              {inf.note ? (
                <NoteRow>
                  <Icon name={inf.note.icon} size={16} color={BADGE_TONE_COLOR[inf.note.tone]} />
                  <Badge label={inf.note.text} tone={inf.note.tone} />
                </NoteRow>
              ) : null}
            </InfCard>
          ))}
        </InfList>
      </Panel>

      {formOpen ? <InfluencerForm key={formParam ?? 'new'} influencer={editing} onClose={onCloseForm} /> : null}
    </PageShell>
  );
};

/**
 * Route entry. Influencers ships behind the acquisition feature flag (see
 * `config/acquisitionChannels`): while it is locked its cards are inert and frosted
 * everywhere, so guard direct-URL access too by bouncing back to Acquisition. Reactive,
 * so a runtime `show_in_progress` flip reveals the page with no reload.
 */
const InfluencersRoute = (props: InfluencersProps): React.ReactElement => {
  const { isLocked } = useAcquisitionChannelGate();
  if (isLocked('influencers')) return <Navigate to={ROUTE.acquisition()} replace />;
  return <Influencers {...props} />;
};

export default InfluencersRoute;
